import type { ApprovalMatrix } from "@edm/domain";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { apiFetch } from "@/lib/api";

const CHOICES = [
  { value: "none", label: "无需审批" },
  { value: "direct_manager", label: "直属上级（人员关系）" },
  { value: "cost_center_owner", label: "成本中心负责人" },
] as const;

const FIELDS: { key: keyof ApprovalMatrix; label: string; hint: string }[] = [
  { key: "assetClaim", label: "资产领用", hint: "资产类物料领用" },
  { key: "assetBorrow", label: "资产借用", hint: "资产类物料借用" },
  { key: "accessoryApply", label: "低值易耗领用", hint: "低值易耗品申请" },
  { key: "consumableIssue", label: "耗材领用", hint: "耗材发放" },
  { key: "softwareLow", label: "软件许可 < 100 元", hint: "金额低于 100 元" },
  { key: "softwareMid", label: "软件许可 100–999.99 元", hint: "100 元至 999.99 元" },
  { key: "softwareHigh", label: "软件许可 ≥ 1000 元", hint: "1000 元及以上" },
];

export function SettingsApprovalPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ApprovalMatrix | null>(null);
  const [saving, setSaving] = useState(false);
  const query = useQuery({
    queryKey: ["approval-matrix"],
    queryFn: () => apiFetch<ApprovalMatrix>("/api/v1/admin/settings/approval"),
  });

  useEffect(() => {
    if (query.data) setForm(query.data);
  }, [query.data]);

  async function save() {
    if (!form) return;
    setSaving(true);
    try {
      await apiFetch("/api/v1/admin/settings/approval", {
        method: "PUT",
        body: JSON.stringify(form),
      });
      await queryClient.invalidateQueries({ queryKey: ["approval-matrix"] });
      toast.success("审批矩阵已保存");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="审批矩阵"
        description="按申请类型与软件金额配置审批角色；保存后新申请立即按新矩阵生成审批链。"
        actions={
          <Button onClick={save} disabled={saving || !form}>
            <Save className="h-4 w-4" />
            {saving ? "保存中…" : "保存矩阵"}
          </Button>
        }
      />

      <Alert variant="info" title="默认矩阵">
        资产领用/借用 → 成本中心负责人；低值易耗 → 直属上级；耗材 → 无需审批；软件按 100 / 1000
        元分档。
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>审批角色</CardTitle>
          <CardDescription>审批人按部门负责人或人员信息中的直属上级字段解析。</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {query.isLoading || !form ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <>
              <div className="grid gap-4 rounded-md border border-border p-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>软件中额阈值（元）</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.softwareMidThresholdCents / 100}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        softwareMidThresholdCents: Math.round(Number(event.target.value) * 100),
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">达到该金额使用“中额”审批角色。</p>
                </div>
                <div className="space-y-2">
                  <Label>软件高额阈值（元）</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.softwareHighThresholdCents / 100}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        softwareHighThresholdCents: Math.round(Number(event.target.value) * 100),
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">必须大于中额阈值。</p>
                </div>
              </div>
              {FIELDS.map((field) => (
                <div
                  key={field.key}
                  className="grid gap-3 border-b border-border pb-3 sm:grid-cols-[220px_1fr] sm:items-center"
                >
                  <div>
                    <Label>{field.label}</Label>
                    <p className="text-xs text-muted-foreground">{field.hint}</p>
                  </div>
                  <select
                    className="h-9 w-full max-w-sm rounded-md border border-input bg-background px-3 text-sm"
                    value={form[field.key]}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        [field.key]: event.target.value as ApprovalMatrix[typeof field.key],
                      })
                    }
                  >
                    {CHOICES.map((choice) => (
                      <option key={choice.value} value={choice.value}>
                        {choice.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
